// Enhanced Offline Status Hook with Read-Only Mode Detection
import React, { useState, useEffect } from 'react';
import { getOfflineStatus, isReadOnlyMode, getOfflineCapabilities } from '../utils/offlineUtils.js';

/**
 * Enhanced hook for offline status and read-only mode detection
 */
export function useOfflineMode() {
  const [isOffline, setIsOffline] = useState(getOfflineStatus());
  const [readOnlyMode, setReadOnlyMode] = useState(isReadOnlyMode());
  const [capabilities, setCapabilities] = useState(null);
  const [lastOnlineTime, setLastOnlineTime] = useState(Date.now());

  // Update capabilities when offline status changes
  const updateCapabilities = async () => {
    try {
      const caps = await getOfflineCapabilities();
      setCapabilities(caps);
    } catch (error) {
      console.warn('Error getting offline capabilities:', error);
      setCapabilities({
        isOffline: getOfflineStatus(),
        readOnlyMode: isReadOnlyMode(),
        availableData: [],
        message: 'Offline mode - Status unknown',
      });
    }
  };

  useEffect(() => {
    // Update capabilities on mount
    updateCapabilities();

    const handleOffline = () => {
      setIsOffline(true);
      setReadOnlyMode(true);
      updateCapabilities();
    };

    const handleOnline = () => {
      setIsOffline(false);
      setReadOnlyMode(false);
      setLastOnlineTime(Date.now());
      updateCapabilities();
    };

    // Listen for network changes
    window.addEventListener('offline', handleOffline);
    window.addEventListener('online', handleOnline);

    // Also listen for visibility changes to check network status
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        // Page became visible, check network status
        const currentOffline = getOfflineStatus();
        if (currentOffline !== isOffline) {
          if (currentOffline) {
            handleOffline();
          } else {
            handleOnline();
          }
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('online', handleOnline);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isOffline]);

  return {
    isOffline,
    readOnlyMode,
    capabilities,
    lastOnlineTime,
    refreshCapabilities: updateCapabilities,
  };
}

/**
 * Hook for checking if specific features should be disabled
 * This hook now accepts offline data as a parameter to avoid multiple hook calls
 */
export function useFeatureDisabled(featureType, offlineData = null) {
  // Only call the hook if offlineData is not provided
  const hookData = useOfflineMode();
  const { isOffline } = offlineData || hookData;

  if (!isOffline) {
    return false;
  }

  const disabledFeatures = ['create', 'edit', 'delete', 'schedule', 'import', 'assign', 'modify'];
  return disabledFeatures.includes(featureType);
}

/**
 * Hook for getting offline-safe function wrappers
 * This hook now accepts offline data as a parameter to avoid multiple hook calls
 */
export function useOfflineSafeFunctions(showError, offlineData = null) {
  // Only call the hook if offlineData is not provided
  const hookData = useOfflineMode();
  const { isOffline } = offlineData || hookData;

  const wrapFunction = (fn, featureType = 'general') => {
    return (...args) => {
      if (isOffline) {
        const messages = {
          create: 'Cannot create new items while offline',
          edit: 'Cannot edit items while offline',
          delete: 'Cannot delete items while offline',
          schedule: 'Cannot schedule sessions while offline',
          import: 'Cannot import data while offline',
          assign: 'Cannot assign users while offline',
          modify: 'Cannot modify data while offline',
          general: 'This feature is not available offline',
        };

        const message = messages[featureType] || messages.general;
        if (showError) {
          showError(message);
        }
        return;
      }
      return fn(...args);
    };
  };

  return { wrapFunction, isOffline };
}

/**
 * Utility function to check if a feature is disabled (non-hook version)
 * Use this when you need to check feature status without using a hook
 */
export function isFeatureDisabled(featureType, isOffline) {
  if (!isOffline) {
    return false;
  }

  const disabledFeatures = ['create', 'edit', 'delete', 'schedule', 'import', 'assign', 'modify'];
  return disabledFeatures.includes(featureType);
}

/**
 * Utility function to create offline-safe function wrappers (non-hook version)
 * Use this when you need to wrap functions without using a hook
 */
export function createOfflineSafeFunctions(showError, isOffline) {
  const wrapFunction = (fn, featureType = 'general') => {
    return (...args) => {
      if (isOffline) {
        const messages = {
          create: 'Cannot create new items while offline',
          edit: 'Cannot edit items while offline',
          delete: 'Cannot delete items while offline',
          schedule: 'Cannot schedule sessions while offline',
          import: 'Cannot import data while offline',
          assign: 'Cannot assign users while offline',
          modify: 'Cannot modify data while offline',
          general: 'This feature is not available offline',
        };

        const message = messages[featureType] || messages.general;
        if (showError) {
          showError(message);
        }
        return;
      }
      return fn(...args);
    };
  };

  return { wrapFunction, isOffline };
}
